/**
 * Υπολογιστικό Εργαλείο Τόκων Υπερημερίας κατά του Ελληνικού Δημοσίου
 * Σύμφωνα με το Άρθρο 45 του Ν. 4607/2019 και το προϊσχύον καθεστώς.
 */

export const PRE_2019_RATE = 6.0;
export const MRO_SPREAD = 3.0;
export const THRESHOLD = 1.0;
export const LAW_EFFECTIVE_DATE_STR = "2019-05-01";

export type InterestType = "yperimerias" | "epidikias";

export interface SuspendedInterestInterval {
  startDate: string; // inclusive 'YYYY-MM-DD'
  endDate: string; // exclusive 'YYYY-MM-DD'
  label: string;
}

export const COVID_EPIDIKIAS_SUSPENSIONS: SuspendedInterestInterval[] = [
  {
    startDate: "2020-03-13",
    endDate: "2020-06-01",
    label: "Αναστολή τόκων επιδικίας 13/03/2020-31/05/2020",
  },
  {
    startDate: "2020-11-07",
    endDate: "2021-04-06",
    label: "Αναστολή τόκων επιδικίας 07/11/2020-05/04/2021",
  },
];

export interface MroEntry {
  date: string; // 'YYYY-MM-DD'
  rate: number; // MRO rate as percentage (e.g. 0.00, 1.25, 4.50)
}

export interface EffectiveRate {
  date: Date; // UTC date when this rate became effective
  mroRate: number; // The MRO component locked in by the 1% trigger rule
  rate: number; // The final applicable rate (MRO + 3.00%)
}

export interface PeriodBreakdown {
  fromDate: string; // 'YYYY-MM-DD'
  toDate: string; // 'YYYY-MM-DD'
  days: number;
  chargeableDays: number;
  mroRate: number | null; // underlying MRO rate, null for pre-2019
  appliedRate: number; // e.g. 6.00, 3.00, 4.25
  interestAccrued: number; // rounded to 2 decimal places
  isInterestSuspended: boolean;
  suspensionReason?: string;
}

export interface CalculationResult {
  principalAmount: number;
  interestType: InterestType;
  // MRO κατά την επίδοση/έναρξη τοκοφορίας. Μετά την 14/09/2022 παραμένει μόνο
  // πληροφοριακό, καθώς το εφαρμοστέο επιτόκιο συγκλίνει στο κοινό state machine.
  filingMroRate: number;
  totalInterest: number;
  totalAmountDue: number;
  baseDaysUsed: number;
  suspendedDays: number;
  periodBreakdown: PeriodBreakdown[];
}

// Ιστορικό MRO αποφάσεων ΕΚΤ (πηγή: ECB key interest rates history).
// Πριν από 15/10/2008 χρησιμοποιείται το MRO minimum bid rate όπου ίσχυαν
// variable rate tenders. Οι ημερομηνίες είναι οι "with effect from" της ΕΚΤ.
// Χρησιμοποιείται ως ενσωματωμένη εφεδρεία όταν το live API (epitokia.mysolon.gr)
// δεν είναι διαθέσιμο — βλ. mro-source.ts.
export const DEFAULT_MRO_HISTORY: MroEntry[] = [
  { date: "1999-01-01", rate: 3.0 },
  { date: "1999-01-22", rate: 3.0 },
  { date: "1999-04-09", rate: 2.5 },
  { date: "1999-11-05", rate: 3.0 },
  { date: "2000-02-04", rate: 3.25 },
  { date: "2000-03-17", rate: 3.5 },
  { date: "2000-04-28", rate: 3.75 },
  { date: "2000-06-09", rate: 4.25 },
  { date: "2000-06-28", rate: 4.25 },
  { date: "2000-09-01", rate: 4.5 },
  { date: "2000-10-06", rate: 4.75 },
  { date: "2001-05-11", rate: 4.5 },
  { date: "2001-08-31", rate: 4.25 },
  { date: "2001-09-18", rate: 3.75 },
  { date: "2001-11-09", rate: 3.25 },
  { date: "2002-12-06", rate: 2.75 },
  { date: "2003-03-07", rate: 2.5 },
  { date: "2003-06-06", rate: 2.0 },
  { date: "2005-12-06", rate: 2.25 },
  { date: "2006-03-08", rate: 2.5 },
  { date: "2006-06-15", rate: 2.75 },
  { date: "2006-08-09", rate: 3.0 },
  { date: "2006-10-11", rate: 3.25 },
  { date: "2006-12-13", rate: 3.5 },
  { date: "2007-03-14", rate: 3.75 },
  { date: "2007-06-13", rate: 4.0 },
  { date: "2008-07-09", rate: 4.25 },
  { date: "2008-10-08", rate: 4.25 },
  { date: "2008-10-09", rate: 4.25 },
  { date: "2008-10-15", rate: 3.75 },
  { date: "2008-11-12", rate: 3.25 },
  { date: "2008-12-10", rate: 2.5 },
  { date: "2009-01-21", rate: 2.0 },
  { date: "2009-03-11", rate: 1.5 },
  { date: "2009-04-08", rate: 1.25 },
  { date: "2009-05-13", rate: 1.0 },
  { date: "2011-04-13", rate: 1.25 },
  { date: "2011-07-13", rate: 1.5 },
  { date: "2011-11-09", rate: 1.25 },
  { date: "2011-12-14", rate: 1.0 },
  { date: "2012-07-11", rate: 0.75 },
  { date: "2013-05-08", rate: 0.5 },
  { date: "2013-11-13", rate: 0.25 },
  { date: "2014-06-11", rate: 0.15 },
  { date: "2014-09-10", rate: 0.05 },
  { date: "2015-12-09", rate: 0.05 },
  { date: "2016-03-16", rate: 0.0 },
  { date: "2019-04-24", rate: 0.0 },
  { date: "2019-09-18", rate: 0.0 },
  { date: "2022-07-27", rate: 0.5 },
  { date: "2022-09-14", rate: 1.25 },
  { date: "2022-11-02", rate: 2.0 },
  { date: "2022-12-21", rate: 2.5 },
  { date: "2023-02-08", rate: 3.0 },
  { date: "2023-03-22", rate: 3.5 },
  { date: "2023-05-10", rate: 3.75 },
  { date: "2023-06-21", rate: 4.0 },
  { date: "2023-08-02", rate: 4.25 },
  { date: "2023-09-20", rate: 4.5 },
  { date: "2024-06-12", rate: 4.25 },
  { date: "2024-09-18", rate: 3.65 },
  { date: "2024-10-23", rate: 3.4 },
  { date: "2024-12-18", rate: 3.15 },
  { date: "2025-02-05", rate: 2.9 },
  { date: "2025-03-12", rate: 2.65 },
  { date: "2025-04-23", rate: 2.4 },
  { date: "2025-06-11", rate: 2.15 },
  { date: "2026-06-17", rate: 2.4 },
];

export class GreekStateInterestCalculator {
  // Σταθερό επιτόκιο 6% πριν τον Ν. 4607/2019
  private readonly PRE_2019_RATE = PRE_2019_RATE;
  // Προσαύξηση 3% επί του MRO
  private readonly MRO_SPREAD = MRO_SPREAD;
  // Κατώφλι σωρευτικής μεταβολής 1%
  private readonly THRESHOLD = THRESHOLD;
  // Ημερομηνία έναρξης εφαρμογής (1η Μαΐου 2019)
  private readonly LAW_EFFECTIVE_DATE = new Date(
    `${LAW_EFFECTIVE_DATE_STR}T00:00:00Z`
  );

  private readonly mroRawHistory: MroEntry[];
  private readonly effectiveRates: EffectiveRate[];
  private readonly firstGlobalTriggerDate: Date | null;

  constructor(mroHistory: MroEntry[] = DEFAULT_MRO_HISTORY) {
    this.mroRawHistory = mroHistory
      .map((entry) => ({ ...entry }))
      .sort((a, b) => a.date.localeCompare(b.date));
    this.effectiveRates = this.buildEffectiveRatesStateList();
    this.firstGlobalTriggerDate =
      this.effectiveRates.find((rate) => rate.date > this.LAW_EFFECTIVE_DATE)
        ?.date ?? null;
  }

  /**
   * State Machine: Αποδομεί το ιστορικό MRO και εντοπίζει τις ημερομηνίες
   * όπου ενεργοποιήθηκε ο κανόνας σωρευτικής μεταβολής 1%.
   */
  private buildEffectiveRatesStateList(): EffectiveRate[] {
    const effectiveHistory: EffectiveRate[] = [];

    // Αρχική βάση: το MRO που ίσχυε την 01/05/2019 (0.00%)
    let currentBase = this.getMroRateForDate(this.LAW_EFFECTIVE_DATE);

    // Πρώτο διάστημα από 01/05/2019
    effectiveHistory.push({
      date: this.LAW_EFFECTIVE_DATE,
      mroRate: currentBase,
      rate: currentBase + this.MRO_SPREAD,
    });

    // Σάρωση ιστορικού μετά την έναρξη ισχύος του νόμου
    for (const mroEvent of this.mroRawHistory) {
      const mroEventDate = this.parseDate(mroEvent.date);
      if (mroEventDate <= this.LAW_EFFECTIVE_DATE) {
        continue;
      }

      // Απόλυτη διαφορά μεταξύ νέας τιμής και κλειδωμένης βάσης
      const rawDiff = Math.abs(mroEvent.rate - currentBase);
      // Διόρθωση σφαλμάτων κινητής υποδιαστολής
      const diff = Math.round(rawDiff * 1000) / 1000;

      if (diff >= this.THRESHOLD) {
        currentBase = mroEvent.rate;
        effectiveHistory.push({
          date: mroEventDate,
          mroRate: currentBase,
          rate: currentBase + this.MRO_SPREAD,
        });
      }
    }

    return effectiveHistory;
  }

  /** Υπολογισμός ημερών μεταξύ δύο ημερομηνιών */
  private getDaysDifference(startDate: Date, endDate: Date): number {
    const msPerDay = 1000 * 60 * 60 * 24;
    return Math.floor(
      (endDate.getTime() - startDate.getTime()) / msPerDay
    );
  }

  /** Μετατροπή ISO ημερομηνίας σε UTC Date */
  private parseDate(dateStr: string): Date {
    return new Date(dateStr + "T00:00:00Z");
  }

  /** Εύρεση του ακατέργαστου MRO που ίσχυε σε μια ημερομηνία */
  private getMroRateForDate(targetDate: Date): number {
    let applicableRate: number | null = null;

    for (const entry of this.mroRawHistory) {
      const entryDate = this.parseDate(entry.date);
      if (targetDate >= entryDate) {
        applicableRate = entry.rate;
      } else {
        break;
      }
    }

    if (applicableRate === null) {
      throw new Error(
        "Δεν υπάρχει διαθέσιμο ιστορικό MRO για την ημερομηνία έναρξης/επίδοσης."
      );
    }

    return applicableRate;
  }

  /** Εύρεση του εφαρμοστέου trigger-locked MRO για μια ημερομηνία */
  private getTriggeredRateForDate(targetDate: Date): EffectiveRate {
    let applicableRate = this.effectiveRates[0]!;
    for (let i = 0; i < this.effectiveRates.length; i++) {
      const currentRate = this.effectiveRates[i]!;
      if (targetDate >= currentRate.date) {
        applicableRate = currentRate;
      } else {
        break;
      }
    }
    return applicableRate;
  }

  private getSuspensionForDate(
    targetDate: Date,
    interestType: InterestType
  ): SuspendedInterestInterval | null {
    if (interestType !== "epidikias") {
      return null;
    }

    for (const interval of COVID_EPIDIKIAS_SUSPENSIONS) {
      const start = this.parseDate(interval.startDate);
      const end = this.parseDate(interval.endDate);
      if (targetDate >= start && targetDate < end) {
        return interval;
      }
    }

    return null;
  }

  private getSuspensionPivotDates(interestType: InterestType): Date[] {
    if (interestType !== "epidikias") {
      return [];
    }

    return COVID_EPIDIKIAS_SUSPENSIONS.flatMap((interval) => [
      this.parseDate(interval.startDate),
      this.parseDate(interval.endDate),
    ]);
  }

  /** Εύρεση εφαρμοστέου επιτοκίου για μια ημερομηνία και συγκεκριμένη επίδοση */
  private getApplicableRateForDate(
    targetDate: Date,
    filingMroRate: number
  ): { appliedRate: number; mroRate: number | null } {
    if (targetDate < this.LAW_EFFECTIVE_DATE) {
      return {
        appliedRate: this.PRE_2019_RATE,
        mroRate: null,
      };
    }

    if (
      !this.firstGlobalTriggerDate ||
      targetDate < this.firstGlobalTriggerDate
    ) {
      return {
        appliedRate: filingMroRate + this.MRO_SPREAD,
        mroRate: filingMroRate,
      };
    }

    const triggeredRate = this.getTriggeredRateForDate(targetDate);
    return {
      appliedRate: triggeredRate.rate,
      mroRate: triggeredRate.mroRate,
    };
  }

  /** Ασφαλής εκτοκισμός με ακέραια αριθμητική (integer scaling) */
  private secureFloatMultiplication(
    principal: number,
    rate: number,
    days: number,
    daysInYear: number
  ): number {
    // Κλιμάκωση: κεφάλαιο *100 (λεπτά), επιτόκιο *10000 (αφαίρεση δεκαδικών)
    const principalInt = Math.round(principal * 100);
    const rateInt = Math.round(rate * 10000);
    // Divisor: 100 (undo principal) * 10000 (undo rate) * 100 (% -> decimal) * daysInYear
    return (principalInt * rateInt * days) / (100_000_000 * daysInYear);
  }

  /**
   * Κύρια μέθοδος υπολογισμού τόκων.
   * Για το συγκεκριμένο εργαλείο η ημερομηνία επίδοσης ταυτίζεται με την
   * ημερομηνία έναρξης τοκοφορίας.
   * @param principal - Οφειλόμενο κεφάλαιο (π.χ. 25683.93)
   * @param startDateStr - Ημερομηνία επίδοσης/έναρξης τοκοφορίας 'YYYY-MM-DD'
   * @param endDateStr - Ημερομηνία λήξης/εξόφλησης 'YYYY-MM-DD'
   * @param daysInYear - Βάση έτους (default 365, εναλλακτικά 360)
   * @param interestType - Υπερημερίας ή επιδικίας. Στους τόκους επιδικίας
   * εφαρμόζονται οι ειδικές COVID αναστολές.
   */
  public calculateInterest(
    principal: number,
    startDateStr: string,
    endDateStr: string,
    daysInYear: number = 365,
    interestType: InterestType = "yperimerias"
  ): CalculationResult {
    const start = this.parseDate(startDateStr);
    const end = this.parseDate(endDateStr);

    if (isNaN(start.getTime()) || isNaN(end.getTime())) {
      throw new Error(
        "Μη έγκυρη μορφή ημερομηνίας. Αναμενόμενη μορφή: 'YYYY-MM-DD'."
      );
    }
    if (start > end) {
      throw new Error(
        "Η ημερομηνία έναρξης δεν δύναται να είναι μεταγενέστερη της ημερομηνίας λήξης."
      );
    }
    if (interestType !== "yperimerias" && interestType !== "epidikias") {
      throw new Error("Μη έγκυρος τύπος τόκου.");
    }

    const filingMroRate = this.getMroRateForDate(start);

    // Συλλογή ημερομηνιών-ορόσημων όπου αλλάζει το επιτόκιο
    const pivotDates: Date[] = [];
    for (const er of this.effectiveRates) {
      pivotDates.push(er.date);
    }
    pivotDates.push(...this.getSuspensionPivotDates(interestType));

    // Ορόσημα εντός της περιόδου
    const seenPivotTimestamps = new Set<number>();
    const relevantPivots = pivotDates
      .filter((d) => {
        if (d <= start || d >= end) {
          return false;
        }
        const timestamp = d.getTime();
        if (seenPivotTimestamps.has(timestamp)) {
          return false;
        }
        seenPivotTimestamps.add(timestamp);
        return true;
      })
      .sort((a, b) => a.getTime() - b.getTime());

    // Κατασκευή υπο-περιόδων (time slicing)
    const periods: { from: Date; to: Date }[] = [];
    let currentStart = start;

    for (const pivot of relevantPivots) {
      periods.push({ from: currentStart, to: pivot });
      currentStart = pivot;
    }
    periods.push({ from: currentStart, to: end });

    let totalInterest = 0;
    let suspendedDays = 0;
    const breakdownTable: PeriodBreakdown[] = [];

    for (const period of periods) {
      const days = this.getDaysDifference(period.from, period.to);
      if (days === 0) continue;

      const rateContext = this.getApplicableRateForDate(
        period.from,
        filingMroRate
      );
      const suspension = this.getSuspensionForDate(period.from, interestType);
      const isInterestSuspended = suspension !== null;
      const chargeableDays = isInterestSuspended ? 0 : days;
      const interestChunk = this.secureFloatMultiplication(
        principal,
        rateContext.appliedRate,
        chargeableDays,
        daysInYear
      );

      totalInterest += interestChunk;
      if (isInterestSuspended) {
        suspendedDays += days;
      }

      breakdownTable.push({
        fromDate: period.from.toISOString().slice(0, 10),
        toDate: period.to.toISOString().slice(0, 10),
        days,
        chargeableDays,
        mroRate:
          rateContext.mroRate === null
            ? null
            : Math.round(rateContext.mroRate * 100) / 100,
        appliedRate: rateContext.appliedRate,
        interestAccrued: Math.round(interestChunk * 100) / 100,
        isInterestSuspended,
        ...(suspension ? { suspensionReason: suspension.label } : {}),
      });
    }

    totalInterest = Math.round(totalInterest * 100) / 100;

    return {
      principalAmount: principal,
      interestType,
      filingMroRate: Math.round(filingMroRate * 100) / 100,
      totalInterest,
      totalAmountDue: Math.round((principal + totalInterest) * 100) / 100,
      baseDaysUsed: daysInYear,
      suspendedDays,
      periodBreakdown: breakdownTable,
    };
  }

  /** Πρόσβαση στα ενεργά επιτόκια (για testing/debugging) */
  public getEffectiveRates(): EffectiveRate[] {
    return [...this.effectiveRates];
  }

  /** Πρόσβαση στο πλήρες ιστορικό MRO (για export/audit) */
  public getMroHistory(): MroEntry[] {
    return this.mroRawHistory.map((entry) => ({ ...entry }));
  }

  /** Ημερομηνία έναρξης ισχύος του Ν. 4607/2019 */
  public getLawEffectiveDate(): Date {
    return new Date(this.LAW_EFFECTIVE_DATE);
  }

  /** Πρώτη ημερομηνία ενεργοποίησης του κοινού μηχανισμού 1% */
  public getFirstGlobalTriggerDate(): Date | null {
    return this.firstGlobalTriggerDate
      ? new Date(this.firstGlobalTriggerDate)
      : null;
  }
}
